/**
 * Attach (or clear) the authenticated user on the active Sentry scope, so
 * every subsequent event carries `user.id`. Identity is the only reliable
 * user signal we have in Sentry: Vercel stamps all server events with
 * `ip:127.0.0.1`, so without this, incident impact counts collapse to
 * "1 user" no matter how many people are affected (LADD-4404).
 *
 * Sends the id only — no email/name — to keep PII out of Sentry while still
 * joining cleanly against Rocky.
 *
 * Design constraints, in order:
 * - **Never throw, never slow the caller.** Called from user-resolution paths
 *   that must not fail because telemetry did.
 * - **Never clear identity by accident.** `setSentryUser` is a no-op on a
 *   falsy id (a malformed Rocky body yields `user.id === undefined`); only
 *   the explicit `clearSentryUser()` (logout) removes the user.
 * - **No-op where the SDK isn't initialized.** The `getClient()` guard skips
 *   runtimes without a Sentry client — notably web's `proxy.ts` runtime,
 *   which Turbopack does not instrument — so we can't misattribute identity
 *   on a scope that outlives a request there. No client ⇒ no events ⇒
 *   nothing to tag.
 * - **Stay out of the proxy bundle.** The dynamic import keeps this module
 *   dependency-free for bundles that don't already carry the SDK;
 *   `@sentry/nextjs` resolves against the consuming app's dependencies, as
 *   with the existing dynamic imports in `current-user/services.ts`. The
 *   module handle is memoized, so only the very first call on a cold
 *   instance applies the user a tick late — after that, `setUser` is
 *   synchronous.
 */
type SentryModule = typeof import("@sentry/nextjs");

let sentryModule: SentryModule | undefined;

function applySentryUser(user: { id: string } | null): void {
  try {
    if (sentryModule) {
      if (sentryModule.getClient()) sentryModule.setUser(user);
      return;
    }
    import("@sentry/nextjs")
      .then((mod) => {
        sentryModule = mod;
        if (mod.getClient()) mod.setUser(user);
      })
      .catch(() => {});
  } catch {
    // Telemetry must never break the caller.
  }
}

export function setSentryUser(userId: string | null | undefined): void {
  if (!userId) return;
  applySentryUser({ id: userId });
}

export function clearSentryUser(): void {
  applySentryUser(null);
}
